
import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, EyeOff, FileText, DatabaseZap, ArrowRightCircle, Smile } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const SafetyPage = () => {
  const fadeIn = (delay = 0, y = 20) => ({
    hidden: { opacity: 0, y: y, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, delay, ease: [0.6, -0.05, 0.01, 0.99] } },
  });

  const safetyPoints = [
    {
      icon: <ShieldCheck size={28} sm:size={36} className="text-cm-green" />,
      title: "100% Anoniem & Veilig",
      text: "Kinderen gebruiken Classbuddy zonder enige persoonlijke gegevens te delen. Hun identiteit blijft altijd volledig beschermd en anoniem."
    },
    {
      icon: <Lock size={28} sm:size={36} className="text-cm-blue" />,
      title: "Geen Gesprekken Opgeslagen",
      text: "Individuele chatgesprekken met de AI-buddy's worden nooit opgeslagen. Privacy en vertrouwelijkheid staan bij ons voorop."
    },
    {
      icon: <EyeOff size={28} sm:size={36} className="text-cm-yellow" />,
      title: "Focus op Thema Analyse",
      text: "We analyseren uitsluitend geanonimiseerde thema's en trends om scholen te voorzien van waardevolle inzichten, nooit de inhoud van specifieke gesprekken."
    },
    {
      icon: <FileText size={28} sm:size={36} className="text-cm-pink" />,
      title: "AVG-Proof (GDPR)",
      text: "Classbuddy is met de grootste zorg ontwikkeld en voldoet volledig aan de AVG (GDPR) richtlijnen om optimale gegevensbescherming te garanderen."
    },
    {
      icon: <DatabaseZap size={28} sm:size={36} className="text-purple-500" />,
      title: "Alleen Anonieme Inzichten",
      text: "Scholen ontvangen uitsluitend geaggregeerde, anonieme data over welzijnsthema's. Deze informatie is nooit herleidbaar tot individuele kinderen."
    },
    {
      icon: <Smile size={28} sm:size={36} className="text-orange-500" />,
      title: "Kindvriendelijk Ontwerp",
      text: "De interface en interacties zijn speciaal ontworpen voor kinderen, met een focus op een positieve en ondersteunende ervaring."
    }
  ];

  return (
    <div className="container mx-auto px-4 section-padding overflow-hidden">
      <motion.div
        initial="hidden"
        animate="visible"
        className="text-center mb-12 sm:mb-16"
      >
        <motion.div variants={fadeIn(0,30)} className="flex justify-center mb-6 sm:mb-8">
          <ShieldCheck size={56} sm:size={72} className="text-cm-green" />
        </motion.div>
        <motion.h1
          variants={fadeIn(0.1, 30)}
          className="page-title px-2"
        >
          Kinderen verdienen <span className="text-gradient-pink-blue font-medium">100% veiligheid</span> en privacy.
        </motion.h1>
        <motion.p
          variants={fadeIn(0.2, 20)}
          className="page-subtitle px-2"
        >
          Bij Classbuddy is de veiligheid en privacy van elk kind onze absolute topprioriteit. Ons platform is gebouwd op een fundament van <strong className="font-semibold">vertrouwen</strong>, <strong className="font-semibold">transparantie</strong> en de strengste normen voor gegevensbescherming.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
        {safetyPoints.map((point, index) => (
          <motion.div
            key={index}
            variants={fadeIn(0.3 + index * 0.1, 20)}
            initial="hidden"
            animate="visible"
            className="glassmorphism-card p-6 sm:p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
            whileHover={{ y: -10, scale: 1.02 }}
          >
            <div className="flex items-start sm:items-center mb-4 sm:mb-5">
             <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-cm-blue-light to-white rounded-full flex items-center justify-center shadow-md mr-3 sm:mr-4">
                {point.icon}
              </div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-sans font-medium text-cm-blue-dark leading-tight">{point.title}</h2>
            </div>
            <p className="text-sm sm:text-base text-cm-gray leading-relaxed flex-grow">{point.text}</p>
          </motion.div>
        ))}
      </div>
      
      <motion.div
        variants={fadeIn(0.9, 20)}
        initial="hidden"
        animate="visible"
        className="mt-12 sm:mt-16 lg:mt-20 p-6 sm:p-8 lg:p-10 bg-gradient-to-r from-cm-green-light/70 via-cm-yellow-light/70 to-cm-pink-light/70 rounded-2xl sm:rounded-3xl shadow-2xl text-center mx-2 sm:mx-0"
      >
        <img  
          alt="Twee AI buddies schudden elkaars handen als symbool voor vertrouwen en samenwerking"
          className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 mx-auto mb-4 sm:mb-6 object-contain"
          src="https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/29de7c7083aa125358f314c680f8dffb.png" />
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-medium text-cm-blue-dark mb-4 sm:mb-5">Uw Vertrouwen, Onze Verantwoordelijkheid</h2>
        <p className="text-base sm:text-lg text-cm-gray mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
          Wij zijn onwrikbaar in onze toewijding aan het creëren van een veilige, ondersteunende en positieve digitale omgeving waar kinderen zich vrij voelen om te delen, te leren en te groeien.
        </p>
        <Button asChild variant="outline" size="lg" className="glassmorphism-button text-cm-blue-dark border-cm-green hover:text-cm-green hover:border-cm-green font-medium text-base sm:text-lg px-6 sm:px-10 py-6 sm:py-7 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300 group min-h-[48px] w-full sm:w-auto">
          <Link to="/contact">
            Vragen over Veiligheid? <ArrowRightCircle className="ml-2 sm:ml-2.5 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </motion.div>
    </div>
  );
};

export default SafetyPage;
